const GIS_SRC = 'https://accounts.google.com/gsi/client';

let scriptPromise = null;
let clientIdPromise = null;

/**
 * Google Identity Services, loaded on demand.
 *
 * The library is fetched the first time something asks for it rather than from index.html:
 * signing in with Google is one of several ways in, and the rest of the app has no use for a
 * third-party script on every page load.
 *
 * This drives the authorization-code flow in a popup, which is what lets us draw our own
 * button: Google's `renderButton` markup lives in a shadow root and their branding terms
 * forbid restyling it, so a button that matches this UI cannot come from their library. The
 * popup hands back a one-time code, the API exchanges it for an ID token using the client
 * secret, and nothing is trusted on this side of the wire.
 */
const GoogleIdentityService = {

	/**
	 * The public client ID, as configured on the API and delivered by the boot
	 * `system-check` request — so it is set in exactly one place, and changing it needs no
	 * rebuild of this bundle. Resolves to null when Google sign-in is switched off.
	 *
	 * @returns {Promise<string|null>}
	 */
	clientId: () => {
		if(!clientIdPromise) {
			// The app renders before that request lands, so wait on it rather than read a
			// field that may not be filled in yet.
			const ready = window.pixwel?.configReady ?? Promise.resolve();
			clientIdPromise = ready
				.catch(() => null)
				.then(() => window.pixwel?.googleClientId || null);
		}

		return clientIdPromise;
	},

	load: () => {
		if(window.google?.accounts?.oauth2) {
			return Promise.resolve(window.google.accounts);
		}

		if(scriptPromise) {
			return scriptPromise;
		}

		scriptPromise = new Promise((resolve, reject) => {
			const script = document.createElement('script');
			script.src = GIS_SRC;
			script.async = true;
			script.defer = true;
			script.onload = () => {
				if(window.google?.accounts?.oauth2) {
					resolve(window.google.accounts);
					return;
				}
				scriptPromise = null;
				reject(new Error('Google Identity Services did not initialize'));
			};
			script.onerror = () => {
				// Deliberately not remembered: a blocked or flaky first load would otherwise
				// leave the button dead for the life of the page, with no way to retry.
				scriptPromise = null;
				reject(new Error('Google Identity Services could not be loaded'));
			};
			document.head.appendChild(script);
		});

		return scriptPromise;
	},

	/**
	 * Opens Google's account chooser and resolves with a one-time authorization code.
	 *
	 * Call it straight from a click handler. The popup only survives the browser's popup
	 * blocker while the click's user activation is still live, which is why the button
	 * resolves the client ID and pre-loads the library on mount instead of doing either on
	 * the way to opening one.
	 *
	 * @returns {Promise<string>} the authorization code to hand the API
	 */
	requestCode: async() => {
		const clientId = await GoogleIdentityService.clientId();

		if(!clientId) {
			throw new Error('google-not-configured');
		}

		const accounts = await GoogleIdentityService.load();

		return new Promise((resolve, reject) => {
			const client = accounts.oauth2.initCodeClient({
				client_id: clientId,
				// `openid email` is all the API reads. `profile` rides along so Google's
				// chooser shows a name and avatar rather than a bare address.
				scope: 'openid email profile',
				// The popup posts the code back to this page instead of redirecting the
				// browser, so there is no redirect URI to register anywhere and the API
				// never handles a browser redirect. Google names that non-redirect
				// `postmessage` when the code is exchanged.
				ux_mode: 'popup',
				callback: (response) => {
					if(response?.code) {
						resolve(response.code);
						return;
					}
					// Closing the popup lands here with `error: 'access_denied'`.
					reject(new Error(response?.error || 'google-cancelled'));
				},
				error_callback: (error) => {
					reject(new Error(error?.type || 'google-unavailable'));
				}
			});

			client.requestCode();
		});
	}
};

export default GoogleIdentityService;
